import algoliasearch from 'algoliasearch'
import { MultipleQueriesQuery, SearchResponse } from '@algolia/client-search'
import { type CollectionProductsPage } from './Products/types'
import { type QueryFunctionContext } from '@tanstack/react-query'
import { BaseFilterValue } from './Filters/types'

import intersection from 'lodash/intersection'
import difference from 'lodash/difference'
import type { ProductHit } from '@/features/product/types'
import { clamp } from '@lounge-fe/ui-kit'

interface QueryOptions extends QueryFunctionContext {
  index: string
  query?: string
  filters: { [key: string]: string }
  tags: string[]
  offset?: number
  length?: number
  sort?: string
  algolia: {
    indexPrefix: string
    appId: string
    apiKey: string
  }
  useSwatches: boolean
}

const requestOptions = {
  getRankingInfo: true,
  distinct: true,
  facets: ['*'],
}

/**
 * This function creates available filters for a set of facets.
 * It takes into account the order of facets specified by the `facetOrdering` parameter.
 * The function reduces the facets to a single object, mapping each facet to a list of its possible values.
 *
 * @param {Object} facets - An object mapping each facet to an object of its possible values.
 * @param {any} facetOrdering - An object specifying the order of values for each facet.
 * @returns {Object} - An object mapping each facet to a list of its possible values.
 */
export const createAvailableFilters = (
  facets: Record<string, Record<string, number>> | undefined,
  facetOrdering: any,
) =>
  Object.entries(facets ?? {}).reduce(
    (accum, [key, value]) => {
      const facetOrder = facetOrdering?.values?.[key]?.order ?? []

      const values = [
        ...intersection(facetOrder, Object.keys(value)),
        ...difference(Object.keys(value), facetOrder),
      ]

      accum[key] = values.map((key) => ({
        label: key,
        value: key,
      }))
      return accum
    },
    {} as { [key: string]: BaseFilterValue[] },
  )

/**
 * This function performs a search query to Algolia, including a query for available facet filters.
 * It then returns a page of product results, along with available filters and the total count of results.
 *
 * TODO: Add tests for this function
 *
 * @param {Object} options - An object containing options for the search query.
 * @returns {Promise<CollectionProductsPage>} - A promise that resolves to a page of search results, including available filters and the total count of results.
 */
export const query = async ({
  query = '',
  filters,
  offset = 0,
  length = 24,
  sort,
  useSwatches = false,
}: QueryOptions): Promise<CollectionProductsPage> => {
  const { algolia } = window.Lounge.integrations
  const { stop_selling_quantity = 0 } = window.Lounge.settings

  const { indexPrefix, apiKey, appId } = algolia

  const algoliaClient = algoliasearch(appId, apiKey)

  const facetFilters = filtersToFacets(filters)

  /**
   * This is intended to be a temporary fix for the issue of filtering stock by options.
   * Essentially, we want to check stock only if the user is filtering by options[x].
   */
  const isFilteringByOptions = Boolean(
    facetFilters.find((facet) =>
      facet.some((f) => f.includes('options') || f.includes('variant_title')),
    ),
  )

  const filterStockQuery =
    indexPrefix === 'en_gb'
      ? `(locations_inventory.18536016 > ${stop_selling_quantity}) OR (locations_inventory.18536016 < 0)`
      : `(inventory_quantity > ${stop_selling_quantity} OR inventory_quantity < 0 )`

  const index = useSwatches
    ? `${indexPrefix}_products_swatches`
    : `${indexPrefix}_products`

  const sortIndex = useSwatches
    ? `${indexPrefix}_products_swatches_sort_${sort}`
    : `${indexPrefix}_products_sort_${sort}`

  const indexName = sort ? sortIndex : index

  const queries: MultipleQueriesQuery[] = [
    {
      indexName,
      query,
      params: {
        ...requestOptions,
        offset,
        length,
        facetFilters: filters?.collections
          ? [`collections:${filters?.collections}`]
          : undefined,
        clickAnalytics: true,
      },
    },
    {
      indexName,
      query,
      params: {
        ...requestOptions,
        facetFilters,
        offset,
        length,
        filters: isFilteringByOptions ? filterStockQuery : '',
        clickAnalytics: true,
      },
    },
  ]

  const {
    results: [facets, products],
  } = await algoliaClient.multipleQueries<ProductHit>(queries)

  const endOffset = Math.min(
    products.nbHits,
    offset + (products.length ?? length),
  )

  return {
    products: products.hits,
    queryID: (products as SearchResponse & { queryID: string }).queryID,
    availableFilters: createAvailableFilters(
      facets.facets,
      facets.renderingContent?.facetOrdering,
    ),
    totalCount: products.nbHits,
    startOffset: offset,
    endOffset: clamp(endOffset, 0, products.nbHits),
  }
}

/**
 * This function is used to transform filter parameters into grouped facets.
 * It maps input options to facet labels.
 *
 * @param {Object | string} input - An object representing the filter parameters. It can also accept a string, which will be returned in a nested array.
 * @param {string} [parentPath=''] - The path of the parent property, used for nesting when transforming objects.
 * @returns {string[][]} - The resulting array of grouped facets. Each group is represented by a subarray of strings.
 */
export const filtersToFacets = (
  rawInput: { [key: string]: any } | string,
  parentPath = '',
): string[][] => {
  const input = JSON.parse(JSON.stringify(rawInput))

  const getFacetFromPath = (path: string, value: any): string => {
    return `${path}:${value}`
  }

  const grouped: { [key: string]: string[] } = {}

  if (input?.price_ratio) {
    const priceRatio = input.price_ratio
      .map((ratio: string) => ratio.split(','))
      .flat()

    grouped['price_ratio'] = priceRatio.map((ratio: string) =>
      getFacetFromPath('price_ratio', ratio),
    )

    delete input.price_ratio
  }

  if (typeof input === 'string') {
    return [[input]]
  }

  // Handle special case for band_size and cup_size
  if (input.options?.band_size && input.options?.cup_size) {
    const variants: string[] = []
    input.options.band_size.forEach((band: string) => {
      input.options.cup_size.forEach((cup: string) => {
        variants.push(getFacetFromPath('variant_title', `${band} / ${cup}`))
      })
    })

    // Combine both variant and size options into one 'options' group
    if (input.options?.size) {
      grouped['options'] = [
        ...(grouped['options'] || []),
        ...variants,
        ...input.options.size.map((size: string) =>
          getFacetFromPath('options.size', size),
        ),
      ]
      delete input.options.size
    } else {
      grouped['options'] = [...(grouped['options'] || []), ...variants]
    }

    delete input.options.band_size
    delete input.options.cup_size
  }

  for (const key in input) {
    const currentPath = parentPath ? `${parentPath}.${key}` : key
    const value = input[key]

    if (Array.isArray(value) && value.length > 0) {
      const facetItems = value.map((item) =>
        getFacetFromPath(currentPath, item),
      )
      grouped[currentPath] = [...(grouped[currentPath] || []), ...facetItems]
    } else if (typeof value === 'object' && value !== null) {
      const subGroup = filtersToFacets(value, currentPath)
      subGroup.forEach((group) => {
        group.forEach((groupItem) => {
          const [groupItemKey] = groupItem.split(':')
          grouped[groupItemKey] = grouped[groupItemKey] || []
          grouped[groupItemKey].push(groupItem)
        })
      })
    } else if (typeof value === 'string' || typeof value === 'number') {
      grouped[currentPath] = [
        ...(grouped[currentPath] || []),
        getFacetFromPath(currentPath, value),
      ]
    }
  }

  return Object.values(grouped).filter((group) => group.length > 0) // filtering out any empty groups
}
